import React, { useEffect, useMemo, useRef, useState } from "react";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import ArenaContainer from "./ArenaContainer";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import { View, useWindowDimensions } from "react-native";
import ChatWebInput from "./Components/ChatWebInput";
import battleHelper from "../../API/Battle/battleHelper";
import Confetti from "react-confetti";
import { useHistory } from "../../Router";
import usePath from "../../Hooks/usePath";
import ArenaHeader from "./Components/ArenaHeader";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useTranslation } from "react-i18next";

const Controller = () => {
  const { height, width } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { params } = usePath();
  const { getActiveWorkspace } = useWorkspaces();
  const [quickQuestions, setQuickQuestions] = useState([]);
  const history = useHistory();
  const { t } = useTranslation();
  const [runConfetti, setRunConfetti] = useState(false);
  const AppToasty = AppToast.useToast();
  const [firstChatId, setFirstChatId] = useState(null);
  const [secondChatId, setSecondChatId] = useState(null);
  const [battleId, setBattleId] = useState(null);
  const [firstAgent, setFirstAgent] = useState(null);
  const [secondAgent, setSecondAgent] = useState(null);
  const [isFirstAIStreaming, setIsFirstAIStreaming] = useState(false);
  const [isSecondAIStreaming, setIsSecondAIStreaming] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [winner, setWinner] = useState(null);
  const [inputText, setInputText] = useState("");
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const { currentTheme, mode, setMode } = AppTheme.useTheme();
  const [isFirstMessage, setIsFirstMessage] = useState(true);

  const [fileUrls, setFileUrls] = useState(null);
  const [fileKeys, setFileKeys] = useState(null);
  const [imageUrls, setImageUrls] = useState(null);
  const [imageKeys, setImageKeys] = useState(null);
  const [audioUrls, setAudioUrls] = useState(null);
  const [audioKeys, setAudioKeys] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);

  const firstCancelRef = useRef(null);
  const secondCancelRef = useRef(null);

  const onCancelGeneration = async () => {
    if (firstCancelRef.current) firstCancelRef.current();
    if (secondCancelRef.current) secondCancelRef.current();
    setIsFirstAIStreaming(false);
    setIsSecondAIStreaming(false);
  };

  const onImageUploaded = (url) => {
    setImageUrl(url);
    setImageKeys((prev) => [...(prev || []), url]);
  };

  const onAudioUploaded = (url) => {
    setAudioUrl(url);
    setAudioKeys((prev) => [...(prev || []), url]);
  };

  const onFileUploaded = (url) => {
    setFileUrl(url);
    setFileKeys((prev) => [...(prev || []), url]);
  };

  const onFilesUploaded = (
    urls,
    presignedUrls,
    indexForRemoval = null,
    removalType = "file",
  ) => {
    if (indexForRemoval !== null) {
      if (removalType === "file" && fileUrls?.length > 0) {
        setFileUrls((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
        setFileKeys((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
      } else if (removalType === "audio" && audioUrls?.length > 0) {
        setAudioUrls((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
        setAudioKeys((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
      } else if (removalType === "image" && imageUrls?.length > 0) {
        setImageUrls((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
        setImageKeys((prev) => {
          const newArray = [...prev];
          newArray.splice(indexForRemoval, 1);
          return newArray;
        });
      }
      return;
    }

    if (!urls || !presignedUrls) return;

    const audioExtensions = [".wav", ".mp3", ".mp4", ".m4a", ".aac", ".ogg"];
    const imageExtensions = [
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".bmp",
      ".webp",
      ".tiff",
      ".tif",
      ".heic",
      ".heif",
      ".svg",
      ".ico",
    ];
    const documentExtensions = [
      ".pptx",
      ".ppt",
      ".json",
      ".pdf",
      ".csv",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".html",
      ".txt",
      ".md",
      ".py",
      ".js",
      ".java",
      ".cpp",
      ".c",
      ".cs",
      ".php",
      ".rb",
      ".swift",
      ".go",
      ".ts",
      ".rs",
      ".kt",
      ".scala",
      ".m",
      ".r",
      ".pl",
      ".sh",
      ".sql",
      ".vb",
      ".lua",
      ".dart",
      ".groovy",
      ".jl",
      ".hs",
      ".f90",
      ".pas",
      ".elm",
      ".clj",
    ];

    const audioFiles = { urls: [], presignedUrls: [] };
    const imageFiles = { urls: [], presignedUrls: [] };
    const documentFiles = { urls: [], presignedUrls: [] };

    urls.forEach((url, index) => {
      const extension = url.toLowerCase().substring(url.lastIndexOf("."));
      if (audioExtensions.includes(extension)) {
        audioFiles.urls.push(url);
        audioFiles.presignedUrls.push(presignedUrls[index]);
      } else if (imageExtensions.includes(extension)) {
        imageFiles.urls.push(url);
        imageFiles.presignedUrls.push(presignedUrls[index]);
      } else if (documentExtensions.includes(extension)) {
        documentFiles.urls.push(url);
        documentFiles.presignedUrls.push(presignedUrls[index]);
      }
    });

    // Always functional-append: consecutive async uploads (e.g. rapid clipboard
    // pastes) must not read stale state and replace the previously added files.
    if (audioFiles.urls.length > 0) {
      setAudioUrls((prev) => [...(prev || []), ...audioFiles.presignedUrls]);
      setAudioKeys((prev) => [...(prev || []), ...audioFiles.urls]);
    }

    if (imageFiles.urls.length > 0) {
      setImageUrls((prev) => [...(prev || []), ...imageFiles.presignedUrls]);
      setImageKeys((prev) => [...(prev || []), ...imageFiles.urls]);
    }

    if (documentFiles.urls.length > 0) {
      setFileUrls((prev) => [...(prev || []), ...documentFiles.presignedUrls]);
      setFileKeys((prev) => [...(prev || []), ...documentFiles.urls]);
    }
  };

  const clearFileState = () => {
    setFileUrls(null);
    setFileKeys(null);
    setImageUrls(null);
    setImageKeys(null);
    setAudioUrls(null);
    setAudioKeys(null);
    setImageUrl(null);
    setAudioUrl(null);
    setFileUrl(null);
  };

  const getRandomAgent = (existingAgent) => {
    const allowedModes = ["chatter", "coder", "retriever"];
    const filteredAgents = state?.agents?.filter(
      (agent) =>
        allowedModes.includes(agent.mode) && agent?.id !== existingAgent?.id,
    );

    if (filteredAgents.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * filteredAgents.length);
    return filteredAgents[randomIndex];
  };

  const onMessageSent = (message) => {
    setInputText(message);
    setShowSplash(false);
    setIsAIStreaming(true);
    setIsFirstAIStreaming(true);
    setIsSecondAIStreaming(true);
    setTimeout(() => {
      setInputText("");
      clearFileState();
    }, 1000);
  };

  const _webInput = useMemo(() => {
    return (
      <ChatWebInput
        placeholder={t("arena.controller.placeholder")}
        setText={setInputText}
        isStreaming={isFirstAIStreaming || isSecondAIStreaming}
        onCancel={onCancelGeneration}
        onSend={onMessageSent}
        allowStop={isFirstAIStreaming || isSecondAIStreaming}
        isChatEmpty={showSplash}
        allowFileUpload={true}
        allowImagesUpload={true}
        allowAudiosUpload={true}
        allowCodeUpload={true}
        onFileUploaded={onFileUploaded}
        onFilesUploaded={onFilesUploaded}
        onImageUploaded={onImageUploaded}
        onAudioUploaded={onAudioUploaded}
        fileUrls={fileUrls}
        setFileUrls={setFileUrls}
        fileUrl={fileUrl}
        setFileUrl={setFileUrl}
        imageUrl={imageUrl}
        setImageUrl={setImageUrl}
        audioUrl={audioUrl}
        setAudioUrl={setAudioUrl}
        imageUrls={imageUrls}
        audioUrls={audioUrls}
      />
    );
  }, [
    isFirstAIStreaming,
    isSecondAIStreaming,
    setInputText,
    showSplash,
    t,
    fileUrls,
    fileUrl,
    imageUrl,
    audioUrl,
    imageUrls,
    audioUrls,
  ]);

  const setupNewBattle = async (randomAgents = false) => {
    try {
      if (randomAgents) {
        const _firstAgent = getRandomAgent(null);
        const _secondAgent = getRandomAgent(_firstAgent);
        if (!_firstAgent?.id || !_secondAgent?.id) {
          AppToasty.show(t("arena.controller.minTwoAgents"), {
            placement: "bottom",
            type: "warning",
          });
          return;
        }
        setFirstAgent(_firstAgent);
        setSecondAgent(_secondAgent);
      } else {
        const _firstAgent = firstAgent;
        const _secondAgent = secondAgent;

        setFirstAgent(null);
        setSecondAgent(null);
        setTimeout(() => {
          setFirstAgent(_firstAgent);
          setSecondAgent(_secondAgent);
        }, [1000]);
      }
      setIsFirstAIStreaming(false);
      setIsSecondAIStreaming(false);
      setWinner(null);
      clearFileState();
    } catch (error) {
      AppToasty.show(t("arena.controller.battleSetupError"), {
        placement: "bottom",
        type: "danger",
      });
      return;
    }
  };

  const createBattle = async () => {
    setIsFirstMessage(true);
    const result = await battleHelper.createBattle_(
      {
        firstAgent: firstAgent.id,
        secondAgent: secondAgent.id,
        createdAt: new Date().toISOString(),
        workspaceID: getActiveWorkspace()?.id,
      },
      getActiveWorkspace()?.workspaceToken,
    );
    setBattleId(result?.data?.createArenaBattle?.id);
  };

  useEffect(() => {
    if (
      firstAgent?.id &&
      secondAgent?.id &&
      firstAgent?.id === secondAgent?.id
    ) {
      AppToasty.show(t("arena.controller.cannotBattleSelf"), {
        placement: "bottom",
        type: "warning",
      });
      return;
    }
    if (firstAgent?.id && secondAgent?.id && getActiveWorkspace()?.id) {
      createBattle();
    }
  }, [secondAgent?.id, firstAgent?.id, getActiveWorkspace()?.id]);

  useEffect(() => {
    if (getActiveWorkspace()?.id && state?.agents?.length > 0) {
      setupNewBattle(true);
    }
  }, [getActiveWorkspace()?.id, state?.agents?.length]);

  useEffect(() => {
    if (firstChatId && secondChatId && battleId) {
      battleHelper.updateBattle(battleId, [
        { property: "firstAgentChatID", value: firstChatId },
        { property: "secondAgentChatID", value: secondChatId },
      ]);
    }
  }, [firstChatId, secondChatId, battleId]);

  useEffect(() => {
    if (winner && battleId) {
      battleHelper.updateBattle(battleId, [
        { property: "winnerID", value: winner },
      ]);
      if (
        state?.agents.find(
          (agent) => agent.id === winner && agent.type === "open",
        )
      ) {
        AppToasty.show(t("arena.controller.toothFairyWon"), {
          type: "success",
          duration: 4000,
          placement: "top",
        });
        setRunConfetti(true);
      } else {
        AppToasty.show(t("arena.controller.closedSourceWon"), {
          type: "danger",
          duration: 4000,
          placement: "center",
        });
      }
    }
  }, [winner]);

  return (
    <View
      style={{
        height: height - 35,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Confetti
        width={width}
        height={height}
        recycle={false}
        numberOfPieces={runConfetti ? 2000 : 0}
        wind={0}
        gravity={0.1}
        initialVelocityX={4}
        initialVelocityY={12}
        opacity={1}
        onConfettiComplete={(confetti) => {
          confetti.reset();
          setRunConfetti(false);
        }}
        colors={[
          currentTheme?.theme?.primary,
          currentTheme?.theme?.primary_soft,
          currentTheme?.theme?.primary_medium,
          currentTheme?.theme?.white,
        ]}
      />
      <ArenaHeader />

      <View
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          padding: 5,
          flex: 1,
          minHeight: 0,
        }}
        key={battleId}
      >
        <ArenaContainer
          key={`first-arena`}
          setWinner={setWinner}
          isWinner={winner === firstAgent?.id}
          agentId={firstAgent?.id}
          workspaceId={getActiveWorkspace()?.id}
          onChatCreated={(chatId) => {
            setFirstChatId(chatId);
          }}
          chatId_prop={firstChatId}
          setAgent={setFirstAgent}
          onStreamFinished={() => {
            setIsFirstAIStreaming(false);
          }}
          _inputText={inputText}
          _agentName={firstAgent?.label}
          isFirstMessage={isFirstMessage}
          setIsFirstMessage={setIsFirstMessage}
          _fileKeys={fileKeys}
          _imageKeys={imageKeys}
          _audioKeys={audioKeys}
          onCancelRef={firstCancelRef}
        />
        <ArenaContainer
          key={`second-arena`}
          setWinner={setWinner}
          isWinner={winner === secondAgent?.id}
          agentId={secondAgent?.id}
          chatId_prop={secondChatId}
          workspaceId={getActiveWorkspace()?.id}
          onChatCreated={(chatId) => {
            setSecondChatId(chatId);
          }}
          setAgent={setSecondAgent}
          _inputText={inputText}
          onStreamFinished={() => {
            setIsSecondAIStreaming(false);
          }}
          _agentName={secondAgent?.label}
          isFirstMessage={isFirstMessage}
          setIsFirstMessage={setIsFirstMessage}
          _fileKeys={fileKeys}
          _imageKeys={imageKeys}
          _audioKeys={audioKeys}
          onCancelRef={secondCancelRef}
        />
      </View>
      {_webInput}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          display: "flex",
          paddingHorizontal: 10,
          flexShrink: 0,
        }}
      >
        <AppText
          wrapperStyle={{
            alignSelf: "center",
            marginBottom: 5,
            gap: 5,
          }}
          style={{
            marginTop: 0,
          }}
          color={currentTheme?.theme?.primary}
          isClickable
          iconSize={16}
          onPress={() => setupNewBattle(false)}
          leftIcon={AppAssets?.icons?.newBattle}
        >
          {t("arena.controller.newRound")}
        </AppText>
        {quickQuestions && quickQuestions?.length > 0 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              flexWrap: "wrap",
              marginBottom: 5,
              gap: 5,
            }}
          >
            {quickQuestions?.map((question, index) => (
              <AppText
                isClickable
                key={index}
                onPress={() => onMessageSent(question)}
                wrapperStyle={{
                  paddingVertical: 5,
                  paddingRight: 10,
                  paddingLeft: 5,
                  borderRadius: 5,
                  borderWidth: 1,
                  borderColor: currentTheme?.theme?.transparent,
                  backgroundColor: currentTheme?.theme?.primary_soft,
                }}
                style={{
                  marginLeft: 5,
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  textDecorationColor: currentTheme?.theme?.primary,
                }}
                color={currentTheme?.theme?.primary}
                leftIcon={AppAssets?.icons?.question_icon}
                iconSize={16}
              >
                {question}
              </AppText>
            ))}
          </div>
        )}

        <AppIcon
          onPress={() => {
            setMode(mode == "dark" ? "light" : "dark");
          }}
          icon={
            mode == "dark"
              ? AppAssets?.icons?.theme_adjust
              : AppAssets?.icons?.theme_adjust
          }
          color={currentTheme?.theme?.primary}
          isClickable
          size={16}
          wrapperStyle={{
            cursor: "pointer",
            rotate: mode == "dark" && "180deg",
            marginRight: 5,
            marginBottom: 4,
          }}
        />
      </View>
    </View>
  );
};

export default Controller;
